import "@/App.css";
import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { LISTS_ENABLED } from "@/lib/featureFlags";
import { AuthProvider } from "@/context/AuthContext";
import { FavoritesProvider } from "@/context/FavoritesContext";
import { Toaster } from "sonner";
import { Loader2 } from "lucide-react";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import AuthCallback from "@/pages/AuthCallback";
import ProtectedRoute from "@/components/ProtectedRoute";
import AppShell from "@/components/AppShell";
import BuildVersionWatcher from "@/components/BuildVersionWatcher";

// Heavy authenticated pages — split into their own chunks so first paint
// only ships Landing + Login + shell code. Charts (Recharts) + admin panel
// + compare pages load on-demand when the user navigates to them.
const Search = lazy(() => import("@/pages/Search"));
const ExchangeMapping = lazy(() => import("@/pages/ExchangeMapping"));
const GapMatrix = lazy(() => import("@/pages/GapMatrix"));
const ProspectRadar = lazy(() => import("@/pages/ProspectRadar"));
const Alerts = lazy(() => import("@/pages/Alerts"));
const KolProfile = lazy(() => import("@/pages/KolProfile"));
const Compare = lazy(() => import("@/pages/Compare"));
const CompareExchanges = lazy(() => import("@/pages/CompareExchanges"));
const ListsIndex = lazy(() => import("@/pages/Lists").then((m) => ({ default: m.ListsIndex })));
const ListDetail = lazy(() => import("@/pages/Lists").then((m) => ({ default: m.ListDetail })));
const ExchangeDetail = lazy(() => import("@/pages/ExchangeDetail"));
const RoiCalculatorPage = lazy(() => import("@/pages/RoiCalculatorPage"));
const Admin = lazy(() => import("@/pages/Admin"));
const AdminUserDetail = lazy(() => import("@/pages/AdminUserDetail"));
const AdminClients = lazy(() => import("@/pages/AdminClients"));
const AdminClientDetail = lazy(() => import("@/pages/AdminClientDetail"));
const AdminUnmetDemand = lazy(() => import("@/pages/AdminUnmetDemand"));
const HiddenCreators = lazy(() => import("@/pages/HiddenCreators"));
const AdminFeatureAccess = lazy(() => import("@/pages/AdminFeatureAccess"));
const UnaffiliatedProfile = lazy(() => import("@/pages/UnaffiliatedProfile"));
const MyRadar = lazy(() => import("@/pages/MyRadar"));
const MyRadarCreator = lazy(() => import("@/pages/MyRadarCreator"));
const MyRadarWorkspace = lazy(() => import("@/pages/MyRadarWorkspace"));
const MyRadarInsights = lazy(() => import("@/pages/MyRadarInsights"));
const AdminGraduations = lazy(() => import("@/pages/AdminGraduations"));
const Privacy = lazy(() => import("@/pages/Privacy"));
const Terms = lazy(() => import("@/pages/Terms"));
const Disclaimer = lazy(() => import("@/pages/Disclaimer"));
const GoogleAuthCallback = lazy(() => import("@/pages/GoogleAuthCallback"));
// v43 — X Creators segment (preview only, behind X_CREATORS_ENABLED).
const XCreatorsList = lazy(() => import("@/pages/XCreatorsList"));
const XCreatorProfile = lazy(() => import("@/pages/XCreatorProfile"));
const BotDetection = lazy(() => import("@/pages/BotDetection"));
const LiveStreamingIntel = lazy(() => import("@/pages/LiveStreamingIntel"));
const Creator360 = lazy(() => import("@/pages/Creator360"));
const LiveStreamerProfile = lazy(() => import("@/pages/LiveStreamerProfile"));

const RouteFallback = () => (
  <div className="min-h-[70vh] flex items-center justify-center text-zinc-500">
    <Loader2 className="animate-spin mr-2" size={16} /> Loading…
  </div>
);

function AppRouter() {
  const location = useLocation();
  // Detect OAuth callback synchronously during render (prevents race conditions)
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Suspense fallback={<RouteFallback />}>
      {/* v41d — silently reloads the tab after a publish on the next
          safe navigation. Zero UI, mounted once above all routes. */}
      <BuildVersionWatcher />
      <Routes>
        <Route path="/" element={<AppShell><Landing /></AppShell>} />
        <Route path="/login" element={<Login />} />
        <Route path="/search" element={<ProtectedRoute><AppShell><Search /></AppShell></ProtectedRoute>} />
        <Route path="/exchange-mapping" element={<ProtectedRoute><AppShell><ExchangeMapping /></AppShell></ProtectedRoute>} />
        <Route path="/gap-matrix" element={<ProtectedRoute><AppShell><GapMatrix /></AppShell></ProtectedRoute>} />
        <Route path="/prospect-radar" element={<ProtectedRoute><AppShell><ProspectRadar /></AppShell></ProtectedRoute>} />
        <Route path="/alerts" element={<ProtectedRoute><AppShell><Alerts /></AppShell></ProtectedRoute>} />
        <Route path="/bot-detection" element={<ProtectedRoute><AppShell><BotDetection /></AppShell></ProtectedRoute>} />
        <Route path="/live-streaming-intel" element={<AppShell><LiveStreamingIntel /></AppShell>} />
        <Route path="/creator-360" element={<AppShell><Creator360 /></AppShell>} />
        <Route path="/live-streaming-intel/:id" element={<AppShell><LiveStreamerProfile /></AppShell>} />
        <Route path="/compare" element={<ProtectedRoute><AppShell><Compare /></AppShell></ProtectedRoute>} />
        <Route path="/lists" element={LISTS_ENABLED ? <ProtectedRoute><AppShell><ListsIndex /></AppShell></ProtectedRoute> : <Navigate to="/my-radar" replace />} />
        <Route path="/lists/:id" element={LISTS_ENABLED ? <ProtectedRoute><AppShell><ListDetail /></AppShell></ProtectedRoute> : <Navigate to="/my-radar" replace />} />
        <Route path="/exchange-compare" element={<ProtectedRoute><AppShell><CompareExchanges /></AppShell></ProtectedRoute>} />
        <Route path="/exchange/:name" element={<ProtectedRoute><AppShell><ExchangeDetail /></AppShell></ProtectedRoute>} />
        <Route path="/roi-calculator" element={<ProtectedRoute><AppShell><RoiCalculatorPage /></AppShell></ProtectedRoute>} />
        <Route path="/kol/:kolId" element={<ProtectedRoute><AppShell><KolProfile /></AppShell></ProtectedRoute>} />
        <Route path="/admin" element={<ProtectedRoute><AppShell><Admin /></AppShell></ProtectedRoute>} />
        <Route path="/admin/clients" element={<ProtectedRoute><AppShell><AdminClients /></AppShell></ProtectedRoute>} />
        <Route path="/admin/clients/:cid" element={<ProtectedRoute><AppShell><AdminClientDetail /></AppShell></ProtectedRoute>} />
        <Route path="/admin/user/:email" element={<ProtectedRoute><AppShell><AdminUserDetail /></AppShell></ProtectedRoute>} />
        <Route path="/admin/unmet-demand" element={<ProtectedRoute><AppShell><AdminUnmetDemand /></AppShell></ProtectedRoute>} />
        <Route path="/admin/hidden-creators" element={<ProtectedRoute><AppShell><HiddenCreators /></AppShell></ProtectedRoute>} />
        <Route path="/admin/feature-access" element={<ProtectedRoute><AppShell><AdminFeatureAccess /></AppShell></ProtectedRoute>} />
        <Route path="/admin/graduations" element={<ProtectedRoute><AppShell><AdminGraduations /></AppShell></ProtectedRoute>} />
        <Route path="/unaffiliated/:channelId" element={<ProtectedRoute><AppShell><UnaffiliatedProfile /></AppShell></ProtectedRoute>} />
        <Route path="/my-radar" element={<ProtectedRoute><AppShell><MyRadar /></AppShell></ProtectedRoute>} />
        <Route path="/my-radar/workspace" element={<ProtectedRoute><AppShell><MyRadarWorkspace /></AppShell></ProtectedRoute>} />
        <Route path="/my-radar/insights" element={<ProtectedRoute><AppShell><MyRadarInsights /></AppShell></ProtectedRoute>} />
        <Route path="/my-radar/creator/:channelId" element={<ProtectedRoute><AppShell><MyRadarCreator /></AppShell></ProtectedRoute>} />
        {/* v43 — X Creators segment. Backend returns 404 when the flag is off,
            so hitting the URL falls through to the "not available" state. */}
        <Route path="/x-creators" element={<ProtectedRoute><AppShell><XCreatorsList /></AppShell></ProtectedRoute>} />
        <Route path="/x-creators/:handle" element={<ProtectedRoute><AppShell><XCreatorProfile /></AppShell></ProtectedRoute>} />
        {/* v38: Google's redirect back for KOL Radar's own OAuth client.
            Public (no ProtectedRoute) — this IS the sign-in step; the
            backend sets the session cookie before we navigate to /search. */}
        <Route path="/auth/google" element={<GoogleAuthCallback />} />
        {/* Legal pages — v38 publicly accessible so Google's app-verification
            checks can reach them without signing in. The /privacy and
            /terms URLs render exactly the same page component in or out
            of the shell, so content and look are identical for signed-in
            users. The in-app shell versions keep working from the footer. */}
        <Route path="/privacy" element={<AppShell><Privacy /></AppShell>} />
        <Route path="/terms" element={<AppShell><Terms /></AppShell>} />
        <Route path="/disclaimer" element={<AppShell><Disclaimer /></AppShell>} />
        {/* Catch-all — any unknown URL falls back to the landing page instead
            of rendering a blank document. */}
        <Route path="*" element={<AppShell><Landing /></AppShell>} />
      </Routes>
    </Suspense>
  );
}

export default function App() {
  return (
    <div className="App">
      <AuthProvider>
        <FavoritesProvider>
          <BrowserRouter>
            <AppRouter />
            <Toaster theme="dark" position="top-right" />
          </BrowserRouter>
        </FavoritesProvider>
      </AuthProvider>
    </div>
  );
}
